import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, ScatterChart as ScatterIcon } from "lucide-react";
import api from "@/lib/api";
import { formatMoney } from "@/lib/prefs";

/**
 * LiveScatterCanvas: a live particle simulation running on the plot area.
 *
 * Instead of static SVG dots, this component renders three animated layers
 * on a single HTML canvas at devicePixelRatio, driven by requestAnimationFrame:
 *
 *   layer 1: a slow sine wave anchored at the 35% food cost guardrail. Its
 *            amplitude scales with the fleet's average food cost, so a
 *            healthy menu breathes softly and a bleeding menu swells hard.
 *   layer 2: a field of champagne bokeh particles drifting rightward,
 *            gently attracted toward each recipe dot. When a particle is
 *            near a dot it swirls around it and is eventually recycled.
 *   layer 3: the recipe dots themselves, with a live radial pulse and a
 *            colour driven by whether the dish is under or over 35%.
 *
 * No Three.js, no Pixi, no extra bundle weight. Pauses when the tab is
 * hidden. Redraws crisply on resize and DPR change.
 */

const PADDING = { top: 24, right: 32, bottom: 48, left: 64 };
const BASE_W = 640;
const BASE_H = 340;

function niceMax(v) {
  if (!Number.isFinite(v) || v <= 0) return 100;
  const pow = Math.pow(10, Math.floor(Math.log10(v)));
  const norm = v / pow;
  const rounded = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
  return rounded * pow;
}

export default function LiveScatterCanvas({ days = 7 }) {
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hover, setHover] = useState(null);
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const particlesRef = useRef([]);
  const lastTsRef = useRef(0);
  const sizeRef = useRef({ w: BASE_W, h: BASE_H });
  const pointsRef = useRef([]);
  const boundsRef = useRef({ xMax: 100, yMax: 100, uMax: 1 });

  // -------- data --------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/analytics/recipe-scatter", { params: { days } });
        if (!cancelled) setPoints(data?.points || []);
      } catch {
        if (!cancelled) setPoints([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days]);

  const bounds = useMemo(() => {
    if (!points.length) return { xMax: 100, yMax: 100, uMax: 1, avgFc: 0 };
    const xMax = niceMax(Math.max(...points.map((p) => p.revenue)));
    const yMax = Math.max(80, niceMax(Math.max(...points.map((p) => p.food_cost_percent))));
    const uMax = Math.max(1, ...points.map((p) => p.units));
    const avgFc = points.reduce((s, p) => s + p.food_cost_percent, 0) / points.length;
    return { xMax, yMax, uMax, avgFc };
  }, [points]);

  useEffect(() => {
    boundsRef.current = bounds;
    pointsRef.current = points;
  }, [points, bounds]);

  // -------- particle seed --------
  const seedParticles = (count) => {
    const arr = [];
    for (let i = 0; i < count; i++) {
      arr.push({
        x: Math.random() * BASE_W,
        y: PADDING.top + Math.random() * (BASE_H - PADDING.top - PADDING.bottom),
        vx: 0.15 + Math.random() * 0.35,
        vy: (Math.random() - 0.5) * 0.05,
        r: 0.8 + Math.random() * 1.6,
        alpha: 0.15 + Math.random() * 0.55,
        life: Math.random() * 400,
      });
    }
    particlesRef.current = arr;
  };

  // -------- rendering --------
  // Depends on `loading` and `points.length > 0` so the effect only runs
  // once the canvas has actually been rendered into the DOM. Without this
  // guard the refs are null at first commit and the animation loop never
  // starts.
  const hasCanvas = !loading && points.length > 0;
  useEffect(() => {
    if (!hasCanvas) return;
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      const rect = container.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      const w = Math.max(320, Math.floor(rect.width));
      const h = Math.max(220, Math.floor(w * (BASE_H / BASE_W)));
      sizeRef.current = { w, h };
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      // Reseed particles based on width so density stays even.
      const count = Math.max(60, Math.min(180, Math.round(w / 8)));
      seedParticles(count);
    };
    resize();
    // Debounce the ResizeObserver callback into the next animation frame
    // so a resize triggered by a resize does not throw a
    // "ResizeObserver loop completed with undelivered notifications"
    // overlay in dev mode.
    let roScheduled = false;
    const ro = new ResizeObserver(() => {
      if (roScheduled) return;
      roScheduled = true;
      requestAnimationFrame(() => {
        roScheduled = false;
        resize();
      });
    });
    ro.observe(container);

    const innerW = () => sizeRef.current.w - PADDING.left - PADDING.right;
    const innerH = () => sizeRef.current.h - PADDING.top - PADDING.bottom;
    const xScale = (v) => PADDING.left + (v / (boundsRef.current.xMax || 1)) * innerW();
    const yScale = (v) => PADDING.top + (1 - v / (boundsRef.current.yMax || 1)) * innerH();

    const draw = (ts) => {
      if (document.hidden) {
        rafRef.current = requestAnimationFrame(draw);
        return;
      }
      const dt = Math.min(48, ts - (lastTsRef.current || ts));
      lastTsRef.current = ts;
      const { w, h } = sizeRef.current;
      const yPad = PADDING.top;
      const yBot = h - PADDING.bottom;
      const xLeft = PADDING.left;
      const xRight = w - PADDING.right;

      // Clear
      ctx.clearRect(0, 0, w, h);

      // --- Sine wave layer (guardrail breathing) ---
      const avgFc = boundsRef.current.avgFc || 0;
      // Wave anchored at the 35% guardrail line. Higher food cost widens
      // the amplitude so a healthy menu is a calm ribbon and a bleeding
      // menu is a stormy tide.
      const guardY = yScale(35);
      const t = ts / 1000;
      const amp = 6 + Math.min(28, Math.max(0, (avgFc - 20) * 1.2));
      ctx.save();
      ctx.beginPath();
      const step = 6;
      for (let x = xLeft; x <= xRight; x += step) {
        const phase = (x / (xRight - xLeft)) * Math.PI * 3 + t * 0.7;
        const y = guardY + Math.sin(phase) * amp * Math.sin(t * 0.35 + x * 0.005);
        if (x === xLeft) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.lineTo(xRight, yBot);
      ctx.lineTo(xLeft, yBot);
      ctx.closePath();
      const grd = ctx.createLinearGradient(0, guardY - amp, 0, yBot);
      grd.addColorStop(0, "rgba(232, 207, 154, 0.16)");
      grd.addColorStop(0.55, "rgba(232, 207, 154, 0.05)");
      grd.addColorStop(1, "rgba(232, 207, 154, 0)");
      ctx.fillStyle = grd;
      ctx.fill();
      ctx.restore();

      // --- Grid lines and Y labels ---
      ctx.save();
      ctx.strokeStyle = "rgba(255,255,255,0.06)";
      ctx.lineWidth = 1;
      ctx.fillStyle = "rgba(240,232,216,0.55)";
      ctx.font = "500 12px 'Inter Tight', system-ui, sans-serif";
      ctx.textAlign = "right";
      ctx.textBaseline = "middle";
      const yTicks = 5;
      for (let i = 0; i <= yTicks; i++) {
        const v = (boundsRef.current.yMax * i) / yTicks;
        const y = yScale(v);
        ctx.beginPath();
        ctx.moveTo(xLeft, y);
        ctx.lineTo(xRight, y);
        ctx.stroke();
        ctx.fillText(Math.round(v) + "%", xLeft - 10, y);
      }
      // X ticks
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      const xTicks = 4;
      for (let i = 0; i <= xTicks; i++) {
        const v = (boundsRef.current.xMax * i) / xTicks;
        const x = xScale(v);
        ctx.fillText(formatMoney(v), x, yBot + 8);
      }
      // Axis titles
      ctx.fillStyle = "rgba(240,232,216,0.6)";
      ctx.font = "500 12px 'Inter Tight', system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "alphabetic";
      ctx.fillText(`Revenue in the last ${days} day${days === 1 ? "" : "s"}`, (xLeft + xRight) / 2, h - 8);
      ctx.save();
      ctx.translate(18, (yPad + yBot) / 2);
      ctx.rotate(-Math.PI / 2);
      ctx.fillText("Food cost percent", 0, 0);
      ctx.restore();

      // 35% guardrail dashed line
      ctx.setLineDash([4, 5]);
      ctx.strokeStyle = "rgba(232,207,154,0.55)";
      ctx.beginPath();
      ctx.moveTo(xLeft, guardY);
      ctx.lineTo(xRight, guardY);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.textAlign = "right";
      ctx.textBaseline = "bottom";
      ctx.fillStyle = "rgba(232,207,154,0.85)";
      ctx.font = "500 11px 'Inter Tight', system-ui, sans-serif";
      ctx.fillText("35% guardrail", xRight - 6, guardY - 4);
      ctx.restore();

      // --- Particle layer ---
      const pts = pointsRef.current;
      const arr = particlesRef.current;
      ctx.globalCompositeOperation = "lighter";
      for (let i = 0; i < arr.length; i++) {
        const p = arr[i];
        // Attract to nearest data point
        let nearestDx = 0, nearestDy = 0, nearestD2 = Infinity;
        for (let j = 0; j < pts.length; j++) {
          const dp = pts[j];
          const dpx = xScale(dp.revenue);
          const dpy = yScale(dp.food_cost_percent);
          const dx = dpx - p.x;
          const dy = dpy - p.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < nearestD2) { nearestD2 = d2; nearestDx = dx; nearestDy = dy; }
        }
        if (nearestD2 < 22500 && nearestD2 > 1) {
          const d = Math.sqrt(nearestD2);
          const pull = 0.06 * (1 - d / 150);
          p.vx += (nearestDx / d) * pull;
          p.vy += (nearestDy / d) * pull;
          // Add a small perpendicular swirl so particles orbit dots.
          p.vx += (-nearestDy / d) * 0.02;
          p.vy += (nearestDx / d) * 0.02;
        }
        // Gentle Brownian
        p.vx += (Math.random() - 0.5) * 0.015;
        p.vy += (Math.random() - 0.5) * 0.02;
        // Damp
        p.vx *= 0.965;
        p.vy *= 0.965;
        // Baseline drift right
        p.vx += 0.012;
        p.x += p.vx * (dt / 16);
        p.y += p.vy * (dt / 16);
        p.life += dt;
        // Recycle
        if (p.x > xRight + 20 || p.y < yPad - 20 || p.y > yBot + 20 || p.life > 8000) {
          p.x = xLeft + Math.random() * 40;
          p.y = yPad + Math.random() * (yBot - yPad);
          p.vx = 0.15 + Math.random() * 0.35;
          p.vy = (Math.random() - 0.5) * 0.05;
          p.life = 0;
          p.alpha = 0.15 + Math.random() * 0.55;
        }
        // Draw
        const rg = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r * 3.2);
        rg.addColorStop(0, `rgba(242, 223, 181, ${p.alpha})`);
        rg.addColorStop(0.55, `rgba(217, 184, 120, ${p.alpha * 0.45})`);
        rg.addColorStop(1, "rgba(166, 133, 71, 0)");
        ctx.fillStyle = rg;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r * 3.2, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalCompositeOperation = "source-over";

      // --- Recipe dots (top layer) ---
      for (let j = 0; j < pts.length; j++) {
        const dp = pts[j];
        const cx = xScale(dp.revenue);
        const cy = yScale(dp.food_cost_percent);
        const baseR = 6 + Math.sqrt(dp.units / (boundsRef.current.uMax || 1)) * 14;
        const pulse = 1 + Math.sin(t * 2 + j * 0.7) * 0.08;
        const good = dp.food_cost_percent <= 35;
        const rim = good ? "rgba(232,207,154,0.95)" : "rgba(244,114,132,0.95)";
        const glow = good ? "rgba(232,207,154,0.35)" : "rgba(244,114,132,0.35)";
        // Outer glow
        const og = ctx.createRadialGradient(cx, cy, baseR * 0.4, cx, cy, baseR * 2.6);
        og.addColorStop(0, glow);
        og.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = og;
        ctx.beginPath();
        ctx.arc(cx, cy, baseR * 2.6 * pulse, 0, Math.PI * 2);
        ctx.fill();
        // Core disk
        const cg = ctx.createRadialGradient(cx - baseR * 0.35, cy - baseR * 0.35, baseR * 0.2, cx, cy, baseR);
        if (good) {
          cg.addColorStop(0, "rgba(255, 240, 210, 1)");
          cg.addColorStop(0.7, "rgba(217, 184, 120, 1)");
          cg.addColorStop(1, "rgba(166, 133, 71, 0.85)");
        } else {
          cg.addColorStop(0, "rgba(255, 230, 232, 1)");
          cg.addColorStop(0.7, "rgba(220, 105, 120, 1)");
          cg.addColorStop(1, "rgba(160, 60, 76, 0.9)");
        }
        ctx.fillStyle = cg;
        ctx.beginPath();
        ctx.arc(cx, cy, baseR * pulse, 0, Math.PI * 2);
        ctx.fill();
        ctx.lineWidth = 1;
        ctx.strokeStyle = rim;
        ctx.stroke();
        // Plate label
        ctx.fillStyle = "rgba(240,232,216,0.85)";
        ctx.font = "500 11px 'Inter Tight', system-ui, sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        const label = dp.name.length > 22 ? dp.name.slice(0, 20) + "..." : dp.name;
        ctx.fillText(label, cx, cy - baseR - 6);
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
    };
  }, [days, hasCanvas]);

  // -------- hover detection --------
  const onMove = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const mx = (e.clientX - rect.left) * (sizeRef.current.w / rect.width);
    const my = (e.clientY - rect.top) * (sizeRef.current.h / rect.height);
    const innerW = sizeRef.current.w - PADDING.left - PADDING.right;
    const innerH = sizeRef.current.h - PADDING.top - PADDING.bottom;
    const xScale = (v) => PADDING.left + (v / (boundsRef.current.xMax || 1)) * innerW;
    const yScale = (v) => PADDING.top + (1 - v / (boundsRef.current.yMax || 1)) * innerH;
    let match = null;
    for (const p of pointsRef.current) {
      const cx = xScale(p.revenue);
      const cy = yScale(p.food_cost_percent);
      const r = 6 + Math.sqrt(p.units / (boundsRef.current.uMax || 1)) * 14 + 6;
      const dx = mx - cx, dy = my - cy;
      if (dx * dx + dy * dy < r * r) {
        match = { ...p, cx, cy, sx: e.clientX - rect.left, sy: e.clientY - rect.top };
        break;
      }
    }
    setHover(match);
  };
  const onLeave = () => setHover(null);

  if (loading) {
    return (
      <div className="rounded-2xl glass-card p-6 h-[420px] flex items-center justify-center text-white/50 text-sm gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading margin scatter
      </div>
    );
  }

  return (
    <div className="rounded-2xl glass-card p-6" data-testid="sales-fc-scatter">
      <div className="flex items-end justify-between mb-4 gap-4">
        <div>
          <div className="label-eyebrow flex items-center gap-2">
            <ScatterIcon className="w-3.5 h-3.5" /> Menu efficiency map
          </div>
          <h3 className="font-display text-2xl mt-1">Sales vs Food Cost</h3>
          <p className="text-sm text-white/60 mt-1 max-w-xl">
            Bigger dots sell more. Dots below the 35 percent line are the plates carrying your kitchen. Particles flow toward the winners, the wave grows when food cost swells.
          </p>
        </div>
        <div className="text-right shrink-0">
          <div className="label-eyebrow">Window</div>
          <div className="font-display text-lg tabular">Last {days}d</div>
        </div>
      </div>

      {points.length === 0 ? (
        <div className="h-[280px] flex flex-col items-center justify-center text-center text-white/60 text-sm gap-1">
          <div className="font-display text-xl text-white/80">No sales yet.</div>
          <div>Log a few Sold plates on your recipes and the wave will start to move.</div>
        </div>
      ) : (
        <div ref={containerRef} className="relative w-full" style={{ minHeight: 320 }}>
          <canvas
            ref={canvasRef}
            onMouseMove={onMove}
            onMouseLeave={onLeave}
            className="w-full block"
            style={{ display: "block" }}
            data-testid="scatter-canvas"
          />
          {hover && (
            <div
              className="absolute pointer-events-none rounded-lg border border-white/12 bg-black/85 backdrop-blur px-3 py-2 text-xs shadow-xl"
              style={{
                left: `${hover.sx}px`,
                top: `${hover.sy}px`,
                transform: "translate(12px, -100%)",
                maxWidth: 240,
              }}
              data-testid="scatter-tooltip"
            >
              <div className="font-medium text-amber-100 mb-1">{hover.name}</div>
              <div className="tabular text-white/85">Revenue: {formatMoney(hover.revenue)}</div>
              <div className="tabular text-white/85">Food cost: {formatMoney(hover.food_cost)} ({hover.food_cost_percent.toFixed(1)}%)</div>
              <div className="tabular text-white/60">Units sold: {hover.units}</div>
              {hover.sell_price > 0 && (
                <div className="tabular text-white/60">Menu price: {formatMoney(hover.sell_price)}</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
